/**
 * Law Firms Service
 * Handles API calls for multi-tenant attorney system
 */
import api from './index';

const lawFirmsService = {
  /**
   * Get current user's law firm info
   * Used to determine access level in attorney dashboard
   * @returns {Promise<object>} Firm info or null
   */
  async getCurrentFirm() {
    const response = await api.get('/law-firms/current');
    return response.data;
  },

  /**
   * Get list of external law firms (for transfer dropdown)
   * Only attorneyadmin can use this
   * @returns {Promise<Array>} List of external firms
   */
  async getExternalFirms() {
    const response = await api.get('/law-firms/external');
    return response.data;
  },

  /**
   * Get all law firms (admin only)
   * @param {object} options Query options
   * @returns {Promise<Array>} List of all firms
   */
  async getAllFirms(options = {}) {
    const params = new URLSearchParams();
    if (options.activeOnly !== undefined) {
      params.append('activeOnly', options.activeOnly.toString());
    }
    const queryString = params.toString();
    const url = queryString ? `/law-firms?${queryString}` : '/law-firms';
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get a single law firm by ID (admin only)
   * @param {string} id Law firm ID
   * @returns {Promise<object>} Law firm
   */
  async getFirm(id) {
    const response = await api.get(`/law-firms/${id}`);
    return response.data;
  },

  /**
   * Create a new law firm (admin only)
   * @param {object} data Law firm data
   * @returns {Promise<object>} Created law firm
   */
  async createFirm(data) {
    const response = await api.post('/law-firms', data);
    return response.data;
  },

  /**
   * Update a law firm (admin only)
   * @param {string} id Law firm ID
   * @param {object} data Fields to update
   * @returns {Promise<object>} Updated law firm
   */
  async updateFirm(id, data) {
    const response = await api.put(`/law-firms/${id}`, data);
    return response.data;
  },

  /**
   * Seed MarkoTM as the first law firm (admin only, one-time setup)
   * @returns {Promise<object>} MarkoTM firm
   */
  async seedMarkoTM() {
    const response = await api.post('/law-firms', { action: 'seed' });
    return response.data;
  },

  /**
   * Upload an agreement for a law firm
   * @param {string} firmId Law firm ID
   * @param {object} data Agreement data { name, fileData (base64), contentType, signedAt }
   * @returns {Promise<object>} Result with uploaded agreement
   */
  async uploadAgreement(firmId, data) {
    const response = await api.post(`/law-firms/${firmId}/agreements`, data);
    return response.data;
  },

  /**
   * Remove an agreement from a law firm
   * @param {string} firmId Law firm ID
   * @param {string} agreementId Agreement ID
   * @returns {Promise<object>} Result
   */
  async removeAgreement(firmId, agreementId) {
    const response = await api.delete(`/law-firms/${firmId}/agreements/${agreementId}`);
    return response.data;
  }
};

export default lawFirmsService;
